import type { ExtraDetails } from "features/dt-basic-setup/single-building/model/types";

import { Grid, Stack } from "@mui/material";
import { Button } from "components/ButtonNew";
import { DropdownSingleSelect } from "components/DropdownSingleSelect";
import { FormRowLabel } from "components/FormRowLabel";
import { YesNoToggleRow } from "components/YesNoToggle";
import { SingleBuildingContext } from "features/dt-basic-setup/single-building/context/AddSingleBuildingContext";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import type { ModernizationElementKey } from "./types";

import { StepColumn } from "../layout/StepColumn";
import { HEATING_KEYS } from "./constants";
import { ModernizationElementsSection } from "./ModernizationElementsSection";

export const BuildingDetailsStep = () => {
  const { t } = useTranslation();
  const { formData, setFormData, setStepNo } = useContext(SingleBuildingContext);

  const extraDetails = formData?.extraDetails as ExtraDetails;

  const heatingOptions = HEATING_KEYS;
  const heatingDisplayOptions = HEATING_KEYS.map((k) => t(`AddBuilding.buildingDetailsStep.heatingOptions.${k}`));

  const updateExtra = (next: Partial<ExtraDetails>) => {
    setFormData({
      ...formData,
      extraDetails: {
        ...extraDetails,
        ...next,
        modernizationElements: next?.modernizationElements ?? extraDetails?.modernizationElements ?? [],
      },
    });
  };

  const updateModernizationElement = (
    key: ModernizationElementKey,
    patch: Partial<{ modernization_date: number; modernized: boolean }>
  ) => {
    updateExtra({
      modernizationElements: {
        ...(extraDetails?.modernizationElements ?? {}),
        [key]: {
          ...(extraDetails?.modernizationElements?.[key] ?? {}),
          ...patch,
        },
      },
    });
  };

  const skipStep = () => setStepNo(5);

  return (
    <Grid className="mx-auto max-w-[90rem]" item xs={12}>
      <Stack className="w-full items-start justify-between">
        <h1 className="font-ds-headline-xl">{t("AddBuilding.buildingDetailsStep.title")}</h1>
        <Button className="" onClick={skipStep} size="sm" variant="secondary">
          {t("AddBuilding.buildingDetailsStep.skipStep")}
        </Button>
      </Stack>
      <p className="mt-2 text-lds-foreground-muted font-ds-body-regular-md">
        {t("AddBuilding.buildingDetailsStep.subtitle")}
      </p>
      <StepColumn className="mt-8" title={t("AddBuilding.buildingDetailsStep.sectionHeating")} xs={12}>
        <FormRowLabel
          className="block w-full text-left"
          label={t("AddBuilding.buildingDetailsStep.heatingSystemLabel")}
        />
        <DropdownSingleSelect
          className="mt-2 w-full"
          clearable={false}
          displayOptions={heatingDisplayOptions}
          hideLabel
          options={heatingOptions}
          selectedValue={extraDetails?.heating_system ?? ""}
          setSelectedValue={(value) => updateExtra({ heating_system: value ?? "" })}
        />
        <FormRowLabel
          className="mt-6 block w-full text-left"
          label={t("AddBuilding.buildingDetailsStep.installationDateLabel")}
        />
        <DropdownSingleSelect
          className="mt-2 w-full"
          clearable={false}
          hideLabel
          options={Array.from(Array(37).keys()).map((i) => 1990 + i)}
          placeholder={t("AddBuilding.buildingDetailsStep.yearPickerPlaceholder")}
          selectedValue={extraDetails?.installation_date}
          setSelectedValue={(value: string | undefined) => updateExtra({ installation_date: Number(value) })}
        />
        <YesNoToggleRow
          className="mt-6"
          onChange={(next) => updateExtra({ modernized: next })}
          question={t("AddBuilding.buildingDetailsStep.modernizationsQuestion")}
          title={t("AddBuilding.buildingDetailsStep.modernizationsTitle")}
          value={extraDetails?.modernized ?? false}
        />
        {extraDetails?.modernized === true && (
          <ModernizationElementsSection
            modernizationElements={extraDetails?.modernizationElements ?? {}}
            updateModernizationElement={updateModernizationElement}
          />
        )}
        <YesNoToggleRow
          className="mt-6"
          onChange={(next) => updateExtra({ mechanical_ventilation: next })}
          question={t("AddBuilding.buildingDetailsStep.mechanicalVentilationQuestion")}
          title={t("AddBuilding.buildingDetailsStep.mechanicalVentilationTitle")}
          value={extraDetails?.mechanical_ventilation ?? false}
        />
        <YesNoToggleRow
          className="mt-6"
          onChange={(next) => updateExtra({ has_cooling: next })}
          question={t("AddBuilding.buildingDetailsStep.coolingQuestion")}
          title={t("AddBuilding.buildingDetailsStep.coolingTitle")}
          value={extraDetails?.has_cooling ?? false}
        />
        <YesNoToggleRow
          className="mt-6"
          onChange={(next) => updateExtra({ has_pv: next })}
          question={t("AddBuilding.buildingDetailsStep.pvQuestion")}
          title={t("AddBuilding.buildingDetailsStep.pvTitle")}
          value={extraDetails?.has_pv ?? false}
        />
      </StepColumn>
    </Grid>
  );
};
